/** When the date row's one-line fit (B-022) must run again (B-030, build 14).
 *
 *  The fit measures text, and Lexend loads with `font-display: swap`: on a cold launch the first
 *  measure reads the fallback font, which draws "Today · Wednesday, Sep 9" narrower than the
 *  slot, and a ResizeObserver never fires again because the slot's width does not change when
 *  the font lands. So the fit re-runs when the fonts are ready, on every later font load, on a
 *  window resize, and when the slot resizes. Returns the stop function; a stopped watch never
 *  calls `fit` again, even for a promise that resolves later. */
export interface DateFitEnv {
  fonts?: {
    ready: Promise<unknown>;
    addEventListener?: (type: string, fn: () => void) => void;
    removeEventListener?: (type: string, fn: () => void) => void;
  } | null;
  win?: {
    addEventListener: (type: string, fn: () => void) => void;
    removeEventListener: (type: string, fn: () => void) => void;
  } | null;
  slot?: Element | null;
}

export function watchDateFit(fit: () => void, env: DateFitEnv): () => void {
  let live = true;
  const run = () => {
    if (live) fit();
  };
  fit();
  env.fonts?.ready.then(run, () => {});
  env.fonts?.addEventListener?.("loadingdone", run);
  env.win?.addEventListener("resize", run);
  let ro: ResizeObserver | null = null;
  if (env.slot && typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(run);
    ro.observe(env.slot);
  }
  return () => {
    live = false;
    env.fonts?.removeEventListener?.("loadingdone", run);
    env.win?.removeEventListener("resize", run);
    ro?.disconnect();
  };
}
